import { WcDatepicker } from 'wc-datepicker/dist/components/wc-datepicker';
import vars from '../../global/vars.js';
import { gsap } from '../../global/gsap.js';

customElements.define('wc-datepicker', WcDatepicker);

const initEventsFilter = (eventsFilter) => {
    const eventListing = document.querySelector('.js-event-listing');
    const eventCards = eventListing.querySelectorAll('.js-event-card');
    const filterGroups = eventsFilter.querySelectorAll('.js-filter-group');
    const filterOverlayToggles = eventsFilter.querySelectorAll('.js-filter-overlay-toggle');
    const datepicker = eventsFilter.querySelector('.js-datepicker');
    const datelabel = eventsFilter.querySelector('.js-date-label');
    const datesWrapper = eventsFilter.querySelector('.js-dates-wrapper');
    const datesCheckbox = eventsFilter.querySelector('.js-dates');
    const filters = eventsFilter.querySelectorAll('[data-filter]');
    const clearDatesBtn = eventsFilter.querySelector('.js-clear-dates');
    const noResults = document.querySelector('.js-no-results');
    const activeBtns = eventsFilter.querySelector('.js-active-btns');
    const activeFilterBar = eventsFilter.querySelector('.js-active-filter-bar');
    const resetBtn = eventsFilter.querySelector('.js-reset-btn');
    const resultsCountEls = eventsFilter.querySelectorAll('.js-results-count');

    let filtersMap;

    // Format date range for display and check checkbox
    const formatRangeLabel = (range) => {
        if (!range || range.length === 0) {
            datesCheckbox.checked = false;
            return 'No date selected';
        }
        const opts = { year: 'numeric', month: 'short', day: 'numeric' };

        if (range.length === 1) {
            datesCheckbox.checked = true;
            datesCheckbox.name = range;
            return new Date(range[0]).toLocaleDateString('en-GB', opts);
        }

        if (range.length === 2) {
            datesCheckbox.checked = true;
            datesCheckbox.name = range;
            const start = new Date(range[0]).toLocaleDateString('en-GB', opts);
            const end = new Date(range[1]).toLocaleDateString('en-GB', opts);
            return `${start} – ${end}`;
        }

        datesCheckbox.checked = false;
        return 'Invalid range';
    };

    // update the date parameter as it is differnet logic to taxonomies
    const setDateInUrl = (range) => {
        const params = new URLSearchParams(window.location.search);

        if (range && range.length) {
            const dateValue = range.length > 1 ? range.join(',') : range[0];
            params.set('date', dateValue); // overwrite existing date
        } else {
            params.delete('date'); // clear date
        }

        const urlString = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({ path: urlString }, '', urlString);
    };

    // delete the selected dates in datepicker and update UI
    const clearDateFilter = () => {
        datepicker.value = null;
        clearDatesBtn.classList.add('inactive');
        datesCheckbox.checked = false;
        datesCheckbox.classList.remove('is-active');
        datesWrapper.classList.add('inactive');
        setDateInUrl(null);
    };

    // Get current filters from URLSearchParams as an object
    const getCurrentFilters = () => {
        const params = new URLSearchParams(window.location.search);
        const filters = {};

        params.forEach((value, key) => {
            if(key !== 'eventView') {
                filters[key] = value.split(',').filter(Boolean);
            }
        });

        return filters;
    };

    // create pill button mark up
    const createButton = (filterLabel, filterName) => {
        const button = document.createElement('button');
        button.classList.add('button', 'button--lightgreen');

        const span = document.createElement('span');
        span.classList.add('icon-wrap');
        span.innerHTML = '<svg class="svg-stroke" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.707031 9.02399L9.02331 0.707704" stroke-width="2"/><path d="M9.02295 9.02399L0.706667 0.707704" stroke-width="2"/></svg>';

        button.textContent = filterLabel;
        button.insertAdjacentElement('beforeend', span);
        button.dataset.filter = filterName;

        return button;
    };

    //rebuild the active pill btns
    const updatePillBtns = () => {
        activeBtns.innerHTML = '';
        let filtersActive = false;
        filters.forEach(filter => {
            if (filter.checked) {
                filtersActive = true;
                let filterLabel;
                let filterName;

                if (filter.dataset.filter == 'date') {
                    filterLabel = formatRangeLabel(filtersMap.date);
                    filterName = 'date';
                } else {
                    filterLabel = filter.dataset.label;
                    filterName = filter.name;
                }

                const button = createButton(filterLabel, filterName);
                activeBtns.append(button);
            }
        });
        activeFilterBar.classList.toggle('is-active', filtersActive);
    };

    // Sync filter checkboxes based on URL filters
    const syncFiltersFromUrl = () => {
        filters.forEach(input => {
            const activeValues = filtersMap[input.dataset.filter] || []; 
            input.checked = activeValues.includes(input.name);
            input.classList.toggle('is-active', input.checked);
        });
    };

    // Update a single filter in the URL
    const updateFilterInUrl = (taxonomy, value, checked) => {
        const params = new URLSearchParams(window.location.search);
        const values = params.get(taxonomy)?.split(',').filter(Boolean) || [];

        if (checked && !values.includes(value)) {
            values.push(value);
        } else if (!checked && values.includes(value)) {
            values.splice(values.indexOf(value), 1);
        }

        if (values.length) {
            params.set(taxonomy, values.join(','));
        } else {
            params.delete(taxonomy);
        }

        const urlString = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState({ path: urlString }, '', urlString);
    };

    // do the filtering
    const filterCards = (firstLoad = false) => {  
        let resultsCount = 0;
        const newMarkers = [];
        const newBounds = new window.google.maps.LatLngBounds();

        eventCards.forEach(card => {
            let visible = true;

            for (const [taxonomy, values] of Object.entries(filtersMap)) {
                if (!values.length) continue;

                if (taxonomy === 'date') {
                    const cardDates = (card.dataset.dates || "")
                        .split(',')
                        .map(d => d.trim())
                        .filter(Boolean);

                    if (!cardDates.length) {
                        visible = false;
                        break;
                    }

                    // parse filter values
                    const [start, end] = values;
                    const startDate = new Date(start);
                    const endDate = end ? new Date(end) : startDate;

                    // check if any card date is within range
                    const matches = cardDates.some(d => {
                        const date = new Date(d);
                        return date >= startDate && date <= endDate;
                    });

                    if (!matches) {
                        visible = false;
                        break;
                    }
                } else {
                    const cardAttr = card.dataset[taxonomy];
                    if (!cardAttr) {
                        visible = false;
                        break;
                    }

                    const cardValues = cardAttr.split(',');
                    if (!values.some(v => cardValues.includes(v))) {
                        visible = false;
                        break;
                    }
                }
            }

            if (visible) {
                resultsCount++;
                
                // Check if global map data exists
                if(window.eventMarkers && window.eventsMap && window.markerCluster) {
                    // Clear existing cluster markers
                    window.markerCluster.clearMarkers();

                    // Loop over markers, hide all and show ones marked as visible by filter
                    window.eventMarkers.forEach(marker => {
                        marker.map = null;

                        if(marker.id === card.dataset.id) {
                            newMarkers.push(marker);
                            marker.map = window.eventsMap;

                            // Update map bounds to suit filtered markers
                            const markerLatLng = new google.maps.LatLng(marker.position.lat, marker.position.lng);
                            newBounds.extend(markerLatLng);
                        }
                    });
                }

                gsap.to(card, { opacity: 1, scale: 1, duration: 0.3, display: "grid" });
            }
        });

        resultsCountEls.forEach(el => { 
            el.textContent = resultsCount;
        });

        if (resultsCount > 0) {
            noResults.style.opacity = 0;

            // Adjust bounds depending on marker count
            if (newMarkers.length === 1) {
                window.eventsMap.setCenter(newBounds.getCenter());
                window.eventsMap.setZoom(15);
            } else {
                window.eventsMap.fitBounds(newBounds);
                window.eventsMap.panToBounds(newBounds);
            }

            // Update markerCluster marker data
            window.markerCluster.addMarkers(newMarkers);
        } else {
            noResults.style.opacity = 1;

            // Hide all markers
            if(window.eventMarkers && window.markerCluster) {
                window.markerCluster.clearMarkers();

                window.eventMarkers.forEach(marker => {
                    marker.map = null;
                });
            }
        }

        const filtersMapCount = Object.keys(filtersMap).length;

        // Scroll if first load and filters are active, or when not first load
        if(firstLoad && filtersMapCount > 0 || !firstLoad) {          
            gsap.to(window, {
                scrollTo: {
                    y: '#filterScroll',
                    offsetY: 20,
                    autoKill: false
                },
                duration: 0.5
            });
        }
    };

    const animateAndFilter = (firstLoad = false) => {
       gsap.to(eventCards, {
            opacity: 0,
            scale: 0.9,
            duration: 0.3,
            display: "none",
            onComplete: () => {
                filterCards(firstLoad);
            }
        });
    };

    const clearAllFilters = () => {
        filters.forEach(filter => {
            const taxonomy = filter.dataset.filter;
            filter.checked = false;
            updateFilterInUrl(taxonomy, filter.name, filter.checked);
        });

        clearDateFilter();

        filtersMap = getCurrentFilters();
        filterCards();

        activeBtns.innerHTML = '';
        activeFilterBar.classList.remove('is-active');
        datesWrapper.classList.add('inactive');
    };

    gsap.set(eventCards, { opacity: 0 });

    // toggle for mobile filter overlay
    filterOverlayToggles.forEach((toggle) => {
        toggle.addEventListener('click', (event) => {
            eventsFilter.classList.toggle('filter-open');
            vars.body.classList.toggle('stop-scrolling');
        });
    });

    // toggles to show/hide filter groups and add Save button on change
    filterGroups.forEach((filterGroup) => {
        const toggles = filterGroup.querySelectorAll('.js-filter-toggle');
        const checkboxes = filterGroup.querySelectorAll('input[data-filter]');
        let firstInteraction = false;

        toggles.forEach(toggle => {
            toggle.addEventListener('click', (event) => {
                event.preventDefault();

                if (filterGroup.classList.contains('filter-expanded')) {
                    filterGroup.classList.remove('filter-expanded');
                    filterGroup.classList.remove('filter-updated');
                } else {
                    filterGroups.forEach(el => {
                        el.classList.remove('filter-expanded');
                        el.classList.remove('filter-updated');
                    });
                    filterGroup.classList.add('filter-expanded');
                    firstInteraction = true;
                }
            });
        });

        checkboxes.forEach(input => {
            input.addEventListener('change', () => {
                if (firstInteraction) {
                    filterGroup.classList.add('filter-updated');
                    firstInteraction = false;
                }
            });
        });
    });

    // close all open filters when clicking elsewhere
    document.addEventListener('click', (event) => {
        filterGroups.forEach(filterGroup => {
            if (
                filterGroup.classList.contains('filter-expanded') &&
                !filterGroup.contains(event.target)
            ) {
                filterGroup.classList.remove('filter-expanded');
            }
        });
    });

    // update date input on date picker select
    datepicker.addEventListener('selectDate', (event) => {
        datelabel.textContent = formatRangeLabel(event.detail);
        datesWrapper.classList.remove('inactive');

        if (event.detail) {
            clearDatesBtn.classList.remove('inactive');
        }
        setDateInUrl(event.detail);

        const button = createButton(formatRangeLabel(event.detail), 'date');
        const activeDateBtn = activeBtns.querySelector(`[data-filter="date"]`);

        if(activeDateBtn) {
            activeDateBtn.remove();
        }
        activeBtns.append(button);
        activeFilterBar.classList.add('is-active');

        const group = event.target.closest('.whats-on-filter__group');
        if (group) {
            group.classList.add('filter-updated');
        }

        // Trigger filtering
        filtersMap = getCurrentFilters();
        animateAndFilter();
    });

    // if dates checkbox is deactivated clear the datepicker
    datesCheckbox.addEventListener('change', (event) => {
        if (!datesCheckbox.checked) {
            clearDateFilter();
        }
    });

    // clear dates btn
    clearDatesBtn.addEventListener('click', (event) => {
        clearDateFilter();
        filtersMap = getCurrentFilters();
        animateAndFilter();
    });

    // Filter checkbox toggle logic
    filters.forEach(filter => {
        const taxonomy = filter.dataset.filter;
        filter.addEventListener('change', () => {
            updateFilterInUrl(taxonomy, filter.name, filter.checked);
            filtersMap = getCurrentFilters();
            updatePillBtns();
            animateAndFilter();
        });
    });


    // Active button event listener
    if (activeBtns) {
        activeBtns.addEventListener('click', (event) => {
            if (event.target.dataset.filter) {
                const removedFilter = event.target.dataset.filter; 
                const inputToUpdate = eventsFilter.querySelector(`.${removedFilter}`); 
                if (inputToUpdate) {
                    inputToUpdate.click();
                }
            }
        });
    }

    // reset btn
    if (resetBtn) {
        resetBtn.addEventListener('click', (event) => {
            event.preventDefault();
            clearAllFilters();
        });
    }

    // Pop state logic
    window.addEventListener('popstate', () => {
        filterGroups.forEach(filterGroup => {
            filterGroup.classList.remove('filter-expanded');
        });
        filtersMap = getCurrentFilters();
        syncFiltersFromUrl();
        updatePillBtns();
        animateAndFilter();
    });

     // Initial sync on page load
    filtersMap = getCurrentFilters();
    syncFiltersFromUrl();
    updatePillBtns();
    animateAndFilter(true);

    // Sync datepicker checkbox if date param exists on first load
    if (filtersMap.date && filtersMap.date.length) {
        datesCheckbox.checked = true;
        datesCheckbox.classList.add('is-active');
        datesWrapper.classList.remove('inactive');
        datelabel.textContent = formatRangeLabel(filtersMap.date);
        const button = createButton(formatRangeLabel(filtersMap.date), 'date');
        activeBtns.append(button);
        activeFilterBar.classList.add('is-active');
    } else {
        datesCheckbox.checked = false;
        datesCheckbox.classList.remove('is-active');
        datesWrapper.classList.add('inactive');
        datelabel.textContent = 'No date selected';
    }
};

export { initEventsFilter as default };
